// 제조사 대시보드 컨트롤 상태(기간/분포/추이) localStorage 영속화 헬퍼.
// 단일 저장 지점 원칙: 로드/저장/기본값/검증/가드 로직을 이 파일에만 둔다.
// Dashboard.vue는 마운트 시 loadDashboardPrefs()로 초기 상태를 하이드레이션하고,
// 각 컨트롤의 커밋 시점마다 saveDashboardPrefs()로 즉시 저장한다.
import dayjs from 'dayjs';

const STORAGE_KEY = 'dashboardPrefsManu';

export const PERIOD_FILTER_PRESETS = [3, 12, 36];
export const DEMOGRAPHICS_MODES = ['birthYear', 'issueAge', 'currentAge'];
export const TREND_GRANULARITIES = ['day', 'month', 'year'];
export const TREND_COMPARES = ['none', 'previous', 'yoy'];
export const TREND_CHART_TYPES = ['bar', 'line'];

// 백엔드 bucket cap(400)에 닿기 전에 프론트에서 먼저 "일별"을 막는 상한.
export const MAX_DAILY_BUCKETS = 400;

// 2026-07 개편 기준 신규 기본값.
export const DEFAULTS = {
  period: { filter: 12 }, // 최근 1년
  demographics: { mode: 'issueAge', bucketSize: 5, excludeUnknown: true },
  trend: { granularity: 'month', compare: 'yoy', chartType: 'bar' },
};

function isValidYYYYMMDD(value) {
  return typeof value === 'string' && /^\d{8}$/.test(value) && dayjs(value, 'YYYYMMDD', true).isValid();
}

function isFutureDate(value) {
  return dayjs(value, 'YYYYMMDD').isAfter(dayjs(), 'day');
}

function validatePeriod(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS.period };
  const { filter } = raw;
  if (filter === 'all') return { filter: 'all' };
  if (filter === 'etc') {
    const { startAt, endAt } = raw;
    if (
      isValidYYYYMMDD(startAt)
      && isValidYYYYMMDD(endAt)
      && !isFutureDate(startAt)
      && !isFutureDate(endAt)
      && startAt <= endAt
    ) {
      return { filter: 'etc', startAt, endAt };
    }
    return { ...DEFAULTS.period };
  }
  if (PERIOD_FILTER_PRESETS.includes(filter)) return { filter };
  return { ...DEFAULTS.period };
}

// 저장된 기간 값을 추이 위젯 가드 판단에 필요한 {isAllPeriod, dayCount} 형태로 변환한다.
export function resolvePeriodMeta(period) {
  if (!period || period.filter === 'all') return { isAllPeriod: true, dayCount: 0 };
  if (period.filter === 'etc') {
    const dayCount = dayjs(period.endAt, 'YYYYMMDD').diff(dayjs(period.startAt, 'YYYYMMDD'), 'day');
    return { isAllPeriod: false, dayCount };
  }
  // 3/12/36개월 프리셋은 개월*30일로 어림잡는다(400일 상한 판단용으로 충분한 근사치).
  return { isAllPeriod: false, dayCount: period.filter * 30 };
}

function validateDemographics(raw) {
  const mode = raw && DEMOGRAPHICS_MODES.includes(raw.mode) ? raw.mode : DEFAULTS.demographics.mode;
  const bucketSize = raw && Number.isInteger(raw.bucketSize) && raw.bucketSize >= 1 && raw.bucketSize <= 50
    ? raw.bucketSize
    : DEFAULTS.demographics.bucketSize;
  // N-3.2: 손상/누락/무효 타입은 기본값(체크됨)으로 조용히 폴백한다.
  const excludeUnknown = raw && typeof raw.excludeUnknown === 'boolean'
    ? raw.excludeUnknown
    : DEFAULTS.demographics.excludeUnknown;
  return { mode, bucketSize, excludeUnknown };
}

// 추이 위젯 가드(일별 400일 상한 + 비교 가능 여부)를 계산한다.
// TrendWidget의 런타임 watch와 로드 시 하이드레이션이 동일 로직을 공유한다.
export function resolveTrendGuards({ granularity, compare, isAllPeriod, dayCount }) {
  let nextGranularity = TREND_GRANULARITIES.includes(granularity) ? granularity : DEFAULTS.trend.granularity;
  let nextCompare = TREND_COMPARES.includes(compare) ? compare : DEFAULTS.trend.compare;

  const dailyDisabled = isAllPeriod || dayCount > MAX_DAILY_BUCKETS;
  if (dailyDisabled && nextGranularity === 'day') {
    nextGranularity = 'month';
  }

  const compareDisabled = isAllPeriod || nextGranularity === 'year';
  if (compareDisabled && nextCompare !== 'none') {
    nextCompare = 'none';
  }

  return { granularity: nextGranularity, compare: nextCompare };
}

function validateTrend(raw, periodMeta) {
  const chartType = raw && TREND_CHART_TYPES.includes(raw.chartType) ? raw.chartType : DEFAULTS.trend.chartType;
  const guarded = resolveTrendGuards({
    granularity: raw && raw.granularity,
    compare: raw && raw.compare,
    isAllPeriod: periodMeta.isAllPeriod,
    dayCount: periodMeta.dayCount,
  });
  return { granularity: guarded.granularity, compare: guarded.compare, chartType };
}

/**
 * localStorage에서 대시보드 컨트롤 상태를 읽어 검증된 형태로 반환한다.
 * 손상된 JSON, 누락/무효 필드는 모두 조용히 신규 기본값으로 폴백한다(예외 없음).
 */
export function loadDashboardPrefs() {
  let raw = null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) raw = JSON.parse(stored);
  } catch (error) {
    raw = null;
  }

  const period = validatePeriod(raw && raw.period);
  const periodMeta = resolvePeriodMeta(period);
  const demographics = validateDemographics(raw && raw.demographics);
  const trend = validateTrend(raw && raw.trend, periodMeta);

  return { period, demographics, trend };
}

/**
 * 현재 컨트롤 상태를 localStorage에 저장한다. 실패(프라이빗 모드, 용량 초과 등)는
 * 대시보드 동작에 필수가 아니므로 조용히 무시한다.
 */
export function saveDashboardPrefs(prefs) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (error) {
    // no-op
  }
}
